import { MarketplacePartnerTierType } from '@atlassian/mpac-types/graphql-gateway-types';

import type { BundleConfig } from './bundleConfig';

import FooterLeftImage from '../assets/service-management/sm_footer_left_image.png';
import FooterRightImage from '../assets/service-management/sm_footer_right_image.png';
import ServiceManagementBundleHeader from '../assets/service-management/sm_header.svg';
import Opportunity1Icon from '../assets/service-management/sm_opportunity1_icon.svg';
import Opportunity2Icon from '../assets/service-management/sm_opportunity2_icon.svg';
import Opportunity3Icon from '../assets/service-management/sm_opportunity3_icon.svg';
import Opportunity4Icon from '../assets/service-management/sm_opportunity4_icon.svg';
import ServiceManagementBundleHero from '../assets/support_flow_bundle_tile.png';

const SERVICE_MANAGEMENT_APPS = [
  {
    title: 'Canned Responses Pro Templates for Jira',
    description: 'Reusable support replies',
    icon: 'https://marketplace.atlassian.com/product-listing/files/d74bd78e-610f-41be-b462-0dac296e8db4?fileType=image&mode=full-fit',
    iconAlt: 'Canned Responses Icon',
    url: 'https://marketplace.atlassian.com/apps/1211016/canned-responses-pro-templates-for-jira',
  },
  {
    title: 'Surveys for Jira Service Management',
    description: 'In-flow customer feedback',
    icon: 'https://marketplace.atlassian.com/product-listing/files/e1f57a16-1270-425c-a51e-ecb289e1d9cf?fileType=image&mode=full-fit',
    iconAlt: 'Surveys Icon',
    url: 'https://marketplace.atlassian.com/apps/1226239/surveys-for-jira-service-management',
  },
  {
    title: 'JSU Automation Suite for Jira Workflows',
    description: 'Service workflow automation',
    icon: 'https://marketplace.atlassian.com/product-listing/files/1473fa99-50c4-4aeb-abd2-fa239e359cec?fileType=image&mode=full-fit',
    iconAlt: 'JSU Automation Icon',
    url: 'https://marketplace.atlassian.com/apps/5048/jsu-automation-suite-for-jira-workflows',
  },
];

const getServiceManagementBenefits = (terminology: string) => [
  {
    title: 'High response and resolution times',
    description:
      'Manual triaging, lack of automation, and inefficient workflows result in long wait times for customers and delayed ticket closure.',
    icon: Opportunity1Icon,
    iconAlt: 'Response Time Icon',
  },
  {
    title: 'Lack of standardisation in request handling',
    description: `Agents handle similar ${terminology} differently when there's no consistency in templates or workflows, leading to inefficiencies and varied service quality.`,
    icon: Opportunity2Icon,
    iconAlt: 'Standardization Icon',
  },
  {
    title: 'Lack of visibility into service workflows',
    description:
      'Without automated processes and workflow insights, teams struggle to track progress, detect inefficiencies, and improve ITSM operations.',
    icon: Opportunity3Icon,
    iconAlt: 'Visibility Icon',
  },
  {
    title: 'Limited understanding of customer experience',
    description:
      'Key feedback is often missed when there are no in-flow mechanisms to gather customer sentiment, leaving teams blind to service quality issues.',
    icon: Opportunity4Icon,
    iconAlt: 'Customer Experience Icon',
  },
];

const getServiceManagementAppsSections = (terminology: string) => [
  {
    title: 'Reduce response time with predefined templates',
    description:
      'Automatically speed up response times by using customizable templates for common requests, ensuring that your team delivers consistent service quickly and efficiently.',
    appDetails: {
      title: 'Canned Responses Pro Templates for Jira',
      vendor: 'by Appfire',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.7/4',
      reviewCount: '102',
      installOrDownloadsText: '2.3k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'ByNR6lCGAB4',
  },
  {
    title: 'Gain valuable customer insights with in-flow surveys',
    description:
      'Collect real-time feedback from customers to gauge satisfaction and identify pain points in the service process, empowering your team to enhance service delivery based on actionable data.',
    appDetails: {
      title: 'Surveys for Jira Service Management',
      vendor: 'by ServiceRocket',
      partnerTier: undefined,
      rating: '3.9/4',
      reviewCount: '7',
      installOrDownloadsText: '250 installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: '3UaCUKYOOsk',
  },
  {
    title: 'Automate workflows for faster ticket resolution',
    description: `Eliminate manual ticket triaging by automating repetitive tasks such as ticket routing and status updates, reducing wait times and freeing up your team to focus on more complex ${terminology}.`,
    appDetails: {
      title: 'JSU Automation Suite for Jira Workflows',
      vendor: 'by Appfire',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.6/4',
      reviewCount: '288',
      installOrDownloadsText: '14.8k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: '1MW9_bSSrOg',
  },
];

export function serviceManagementBundleConfig(enableIssueTerminology: boolean): BundleConfig {
  const terminology = enableIssueTerminology ? 'work items' : 'issues';

  return {
    hero: {
      breadcrumbTitle: 'Service Management Bundle',
      title: 'Support Flow Optimizer',
      description:
        'Boost efficiency and reduce resolution times with standardized workflows and enhanced support.',
      requestButton: 'Request now',
      headerImage: ServiceManagementBundleHeader,
      headerImageAlt: 'Service Management Bundle Header Image',
      heroImage: ServiceManagementBundleHero,
      heroImageAlt: 'Service Management Bundle Hero Image',
    },
    apps: SERVICE_MANAGEMENT_APPS,
    benefits: {
      title: 'Clear the hurdles to high-quality support',
      description:
        'From slow response times to lack of visibility, support challenges can have a significant impact on teams and customers.',
      items: getServiceManagementBenefits(terminology),
    },
    appsSection: {
      title: 'An easier way to work better together',
      description:
        'Optimize service workflows, reduce response times, and ensure consistent, high-quality customer interactions.',
      sections: getServiceManagementAppsSections(terminology),
    },
    footer: {
      title: 'Want to deliver faster, more consistent service?',
      description:
        'Reduce response times, standardise workflows, and capture customer feedback effortlessly.',
      requestButton: 'Request now',
      leftImageAlt: 'Team support left',
      rightImageAlt: 'Team support right',
      leftImage: FooterLeftImage,
      rightImage: FooterRightImage,
    },
  };
}
